import React from "react";
import { z } from "zod/v3";
import { useStore } from "@tanstack/react-form";

import { genid } from "@workspace/shared/utils";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { Textarea } from "@workspace/ui/components/textarea";

import type { TabInput } from "../../types";
import { useEposContext } from "../epos-context";
import { CountInput } from "./count-input";

const TabInputSchema = z.object({
	tabId: z.string().min(1),
	number: z.number().int().min(1),
	name: z.string().max(50),
	section: z.string().min(1),
	covers: z
		.number()
		.int()
		.min(1, "Covers must be at least 1")
		.max(999, "Maximum 999 covers"),
	notes: z.string().max(200, "Maximum 200 characters"),
	createdAt: z.string(),
});

type TabFormValues = z.infer<typeof TabInputSchema>;

function getTodayTabs(
	tabs: { number: number; createdAt?: string | null }[],
	timeZone: string,
) {
	const todayStr = new Date().toLocaleDateString("en-CA", { timeZone });
	return tabs.filter((t) => {
		if (!t.createdAt) return false;
		const tabDate = new Date(t.createdAt).toLocaleDateString("en-CA", {
			timeZone,
		});
		return tabDate === todayStr;
	});
}

export function CreateTabDialog({
	isOpen,
	onOpenChange,
}: {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const {
		tabs,
		createTab,
		isCreatingTab,
		setActiveTabId,
		createNewOrder,
		setView,
		timeZone,
	} = useEposContext();

	const nextNumber = React.useMemo(() => {
		const todayTabs = getTodayTabs(tabs, timeZone);
		if (todayTabs.length === 0) return 1;
		return Math.max(...todayTabs.map((t) => t.number)) + 1;
	}, [tabs, timeZone]);

	const form = useAppForm({
		defaultValues: {
			tabId: genid(),
			number: nextNumber,
			name: "",
			section: "default",
			covers: 1,
			notes: "",
			createdAt: new Date().toISOString(),
		} satisfies TabFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: TabInputSchema,
		},
		onSubmit: async ({ value }) => {
			const payload: TabInput = {
				...value,
				name: value.name || null,
				section: "default",
				tabId: value.tabId,
				number: value.number,
				covers: value.covers,
				createdAt: value.createdAt || new Date().toISOString(),
				notes: value.notes || null,
			};

			const result = await createTab(payload);
			if (!result.success) return;

			const tabId = payload.tabId!;
			setActiveTabId(tabId);
			onOpenChange(false);

			// Fire-and-forget: createNewOrder sets currentOrder which would
			// hide the Activity subtree, but the dialog is now rendered outside
			// Activity so onOpenChange(false) above commits immediately.
			createNewOrder({ tabId }).then(() => setView("products"));
		},
	});

	const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
	const isBusy = isCreatingTab || isSubmitting;

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			if (open) {
				form.reset();
				form.setFieldValue("tabId", genid());
				form.setFieldValue("number", nextNumber);
				form.setFieldValue("section", "default");
				form.setFieldValue("covers", 1);
				form.setFieldValue("name", "");
				form.setFieldValue("notes", "");
				form.setFieldValue("createdAt", new Date().toISOString());
			}

			onOpenChange(open);
		},
		[form, nextNumber, onOpenChange],
	);

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>New Tab</DialogTitle>
					<DialogDescription>Create a tab for a customer.</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					<form.AppField name="tabId">
						{(field) => (
							<field.FormItem className="hidden">
								<field.FormLabel>Tab Id</field.FormLabel>
								<field.FormControl>
									<Input
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="section">
						{(field) => (
							<field.FormItem className="hidden">
								<field.FormLabel>Section</field.FormLabel>
								<field.FormControl>
									<Input
										value={field.state.value || "default"}
										onChange={(e) => field.handleChange(e.target.value)}
										disabled
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="createdAt">
						{(field) => (
							<field.FormItem className="hidden">
								<field.FormLabel>Created At</field.FormLabel>
								<field.FormControl>
									<Input
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="number">
						{(field) => (
							<field.FormItem className="hidden">
								<field.FormControl>
									<Input
										type="number"
										value={field.state.value}
										onChange={(e) => field.handleChange(Number(e.target.value))}
										disabled
									/>
								</field.FormControl>
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Tab name</field.FormLabel>
								<field.FormControl>
									<Input
										placeholder="e.g. Table 12 (optional)"
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
										maxLength={50}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="covers">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Covers</field.FormLabel>
								<field.FormControl>
									<CountInput
										min={1}
										max={999}
										value={field.state.value}
										onValueChange={field.handleChange}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="notes">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Notes</field.FormLabel>
								<field.FormControl>
									<Textarea
										placeholder="Optional notes"
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
										maxLength={200}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={isBusy}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isBusy}>
							{isBusy && <Spinner />}
							Create Tab
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
}
